import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import { Team } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Team/fetchTeams/types";
import {
  createTeamAction,
  deleteTeamAction,
  fetchTeamsAction,
  updateTeamAction,
} from "@/modules/administration/features/EmployeeDirectoryManagement/slices/teamSlice";
import { useNotification } from "@/context/NotificationContext";

export const useTeamManagementPage = () => {
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();

  const list = useAppSelector(
    (state: RootState) => state.employeeDirectoryTeam.list,
  );
  const loading = useAppSelector(
    (state: RootState) => state.employeeDirectoryTeam.loadingList,
  );
  const error = useAppSelector(
    (state: RootState) => state.employeeDirectoryTeam.listError,
  );
  const loadingCreate = useAppSelector(
    (state: RootState) => state.employeeDirectoryTeam.loadingCreate,
  );
  const loadingUpdate = useAppSelector(
    (state: RootState) => state.employeeDirectoryTeam.loadingUpdate,
  );
  const loadingDelete = useAppSelector(
    (state: RootState) => state.employeeDirectoryTeam.loadingDelete,
  );

  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [editTarget, setEditTarget] = useState<Team | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Team | null>(null);

  const refetch = useCallback(() => {
    dispatch(fetchTeamsAction());
  }, [dispatch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const openCreateDrawer = useCallback(() => setShowCreateDrawer(true), []);
  const closeCreateDrawer = useCallback(() => setShowCreateDrawer(false), []);
  const openEditDrawer = useCallback((team: Team) => setEditTarget(team), []);
  const closeEditDrawer = useCallback(() => setEditTarget(null), []);
  const openDeleteModal = useCallback(
    (team: Team) => setDeleteTarget(team),
    [],
  );
  const closeDeleteModal = useCallback(() => setDeleteTarget(null), []);

  const handleCreate = useCallback(
    (values: { name: string }) => {
      dispatch(
        createTeamAction(values, () => {
          showNotification?.(
            "Success",
            "Team created successfully.",
            "success",
          );
          closeCreateDrawer();
          refetch();
        }),
      );
    },
    [dispatch, closeCreateDrawer, refetch, showNotification],
  );

  const handleUpdate = useCallback(
    (values: { name: string }) => {
      if (!editTarget) return;
      dispatch(
        updateTeamAction(editTarget.uuid, values, () => {
          showNotification?.(
            "Success",
            "Team updated successfully.",
            "success",
          );
          closeEditDrawer();
          refetch();
        }),
      );
    },
    [dispatch, editTarget, closeEditDrawer, refetch, showNotification],
  );

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    dispatch(
      deleteTeamAction(deleteTarget.uuid, () => {
        showNotification?.("Success", "Team deleted successfully.", "success");
        closeDeleteModal();
        refetch();
      }),
    );
  }, [dispatch, deleteTarget, closeDeleteModal, refetch, showNotification]);

  return {
    list,
    loading,
    error,
    loadingCreate,
    loadingUpdate,
    loadingDelete,
    showCreateDrawer,
    editTarget,
    deleteTarget,
    openCreateDrawer,
    closeCreateDrawer,
    openEditDrawer,
    closeEditDrawer,
    openDeleteModal,
    closeDeleteModal,
    handleCreate,
    handleUpdate,
    handleDelete,
  };
};
